import i18n from '@/i18n';
/**
 * 刊登图转 Tiktok 平台地址：下载原图 → 按货品图规则压缩 → uploadProductPic（bg.goods.image.upload.global）。
 * 平台用上传 base64 原图做尺寸/格式校验；前端须先压成合规图。不再复用 Shein transform-pic。
 */

import { tiktokPlatformAPI } from '@/utils/api';
import { resolveImageUrlToBlob, stripImageFileExtension } from '@/utils/imageUrlUtils';
import { toManagedCanonicalUploadsUrl } from '@/utils/imageDisplayAccess';
import {
  TIKTOK_PRODUCT_IMAGE_MAX_BYTES,
  prepareTiktokProductImage,
} from '@/tiktok-platform/utils/tiktokProductImage';
import { toCanonicalUploadsPath } from '@/tiktok-platform/modules/products/components/publishImageUploadThumbSrc';

/** 刊登预组转图浏览器并行度 */
export const LISTING_PREPARE_CONVERT_CONCURRENCY = 20;

function asText(value) {
  return String(value || '').trim();
}

function fileNameFromUrl(url) {
  const raw = asText(url);
  if (!raw) return 'product.jpg';
  try {
    const path = new URL(raw, 'https://local.invalid').pathname;
    return decodeURIComponent((path.split('/').pop() || '').split('?')[0]) || 'product.jpg';
  } catch {
    return (raw.split('/').pop() || '').split('?')[0] || 'product.jpg';
  }
}

export function looksLikeTiktokImageTooLargeError(error) {
  const message = String(error?.message || error || '').toLowerCase();
  if (!message) return false;
  return message.includes('3mb')
    || message.includes('3 mb')
    || message.includes('too large')
    || message.includes('size limit')
    || (message.includes('exceed') && message.includes('size'))
    || (message.includes('超过') && (message.includes('大') || message.includes('大小') || message.includes('限制')));
}

function packConverted(source, nameHint, res) {
  // Create Product / listing_check 必须用 Upload Image 返回的 uri，不能用 CDN url 冒充
  const uri = asText(res?.uri || res?.tiktokUri);
  const tiktokUrl = asText(res?.tiktokUrl || res?.url) || uri;
  if (!uri) return null;
  const rawName = asText(nameHint) || fileNameFromUrl(source);
  const rawIpe = asText(res?.ipeUrl);
  const rawPublic = asText(res?.publicUrl);
  const durable = toCanonicalUploadsPath(rawIpe)
    || toCanonicalUploadsPath(rawPublic)
    || toManagedCanonicalUploadsUrl(source)
    || rawIpe
    || rawPublic
    || source;
  return {
    name: stripImageFileExtension(rawName) || rawName,
    tiktokUrl,
    uri,
    ipeUrl: durable,
    publicUrl: durable,
    previewUrl: durable,
    url: tiktokUrl,
  };
}

/**
 * @param {{ shopId: string|number, url: string, imageType?: number, nameHint?: string, forPackage?: boolean, useCase?: string }} params
 * @returns {Promise<{ tiktokUrl: string, uri: string, ipeUrl: string, publicUrl: string, previewUrl: string, name: string }|null>}
 */
export async function convertListingImageToTiktok({
  shopId,
  url,
  imageType = 1,
  nameHint = '',
  forPackage = false,
  useCase = '',
} = {}) {
  const source = asText(url);
  if (!shopId || !source) return null;
  // 即使源图已是平台 CDN，Create Product 仍要 material uri；禁止短路跳过 Upload Image
  // （旧逻辑 looksLikeTiktokHostedUrl 会丢 uri，listing_check 报 12052722）

  const blob = await resolveImageUrlToBlob(source);
  if (!blob) return null;
  const rawName = asText(nameHint) || fileNameFromUrl(source);
  const file = new File(
    [blob],
    rawName.includes('.') ? rawName : `${rawName || 'product'}.jpg`,
    { type: blob.type || 'image/jpeg', lastModified: Date.now() },
  );
  const prepared = forPackage
    ? file
    : await prepareTiktokProductImage(file, { requireAspect: false });
  if (prepared.size > TIKTOK_PRODUCT_IMAGE_MAX_BYTES) {
    throw new Error(i18n.t('workbench:ipeUtilError.imageOver10mbCompress'));
  }
  const res = await tiktokPlatformAPI.uploadProductPic(shopId, prepared, {
    imageBizType: forPackage ? 1 : 0,
    // imageType 仅前端语义（主图/细节），Tiktok 货品图统一 imageBizType=0
    imageType,
    // 用途枚举：SKU 变体图/尺码表图/认证图按需覆盖
    useCase,
    // 刊登/空间选图：原图已在图片空间，勿重复登记
    fromImageSpace: true,
  });
  const packed = packConverted(source, nameHint, {
    ...res,
    // 优先保留空间原图地址
    ipeUrl: res?.ipeUrl || toCanonicalUploadsPath(source) || toManagedCanonicalUploadsUrl(source) || source,
    publicUrl: res?.publicUrl || toCanonicalUploadsPath(source) || source,
  });
  if (!packed?.uri) {
    throw new Error(i18n.t('workbench:ipeUtilError.platformConvertNoUri'));
  }
  return packed;
}
